import {
  ref,
  shallowRef,
  markRaw,
  provide,
  inject,
  watch,
  type InjectionKey,
  type Ref,
  type ShallowRef,
} from 'vue'
import { isNavigationFailure, NavigationFailureType, type NavigationFailure } from 'vue-router'
import type { Page, PipeFullReleaseDto } from '@api/types.gen'
import { hasInjectionContext } from 'vue'
import type { PipeIntegration } from '@render/integrations'
import { isRtlLanguage } from '@render/lib/isRtlLanguage'
import { formatLocalIsoDate } from '@render/lib/dateCalc'
import {
  readStoredConsent,
  isConsentPending,
  COOKIE_CONSENT_PENDING_VAR_KEY,
} from '@render/lib/cookieConsent'
import {
  readStoredIngressTraceIds,
  writeStoredIngressTraceIds,
  readUrlIngressTraceId,
  resolveIngressTraceIds,
  ORIGIN_INGRESS_TRACE_ID_VAR_KEY,
  CURRENT_INGRESS_TRACE_ID_VAR_KEY,
} from '@render/lib/ingressTrace'
import { STOCK_EVENT_PROCESSORS } from './usePipeEventHandlers'
import type { ExitIntentTrigger } from './useExitIntent'

// Pipe-level event channel names
export const PIPE_EVENTS = {
  errorTrack: 'error:track',
  // A funnel-critical network call failed (e.g. pipeorders create-order).
  // Consumed by the NetworkErrorBanner overlay mounted in the build's app.vue
  // and the editor's PreviewApp.
  networkError: 'error:network',
  // Command: re-read tracking cookies into `var.global.trackingProperties`.
  // Emitted immediately before anything serializes that snapshot outbound (the
  // pipeorders order POST, the payments session, the payment:* payloads);
  // handled by TRACKING_PROPERTIES, which owns the variable. Goes over the bus
  // rather than through an import because that integration is optional per
  // pipe — with it disabled the emit has no listener.
  //
  // Emitters rely on the effect being in place once `emit` returns: `emit` is a
  // synchronous `dispatchEvent` and `subscribe` invokes the callback inline. Any
  // async handler on this channel breaks that.
  trackingRefresh: 'tracking:refresh-cookies',
  // Command: render `value` in place of the live value of interpolation key
  // `key` for `durationMs`, flashing on arrival. Payload: `VariableTickPayload`
  // (useVariableTicks.ts), which owns the renderer side of the channel.
  variableTick: 'variable:tick',
  // A variable was written through `setData`: every visitor choice (a select, a
  // slider, a typed answer, a `set_data` action) ends up here. Payload:
  // `VariableSetPayload`. Fires on every write, including each keystroke and
  // each slider tick, so a subscriber must stay cheap.
  variableSet: 'variable:set',
  // One run of an event's processor chain (`getEventListeners`): start before its first
  // processor, end after its last, halted or not. Payloads: `ActionRunPayload` and
  // `ActionEndPayload`. The stock processors put the run's `actionId` on what they cause
  // (`variable:set`, `navigation:request`, `tracking:custom-event`), so a listener can tie
  // those back to what set the run off instead of guessing from timing.
  actionStart: 'action:start',
  actionEnd: 'action:end',
  // A page change was asked for (`navigateToPageId`), before the router moves.
  // Payload: `NavigationRequestPayload`.
  navigationRequest: 'navigation:request',
} as const

export interface VariableSetPayload {
  /** `group.name`, without the `var.` prefix. */
  key: string
  value: unknown
  /** The `set_data` processor's source (`ref/…/nodeId`); absent for a component's own write. */
  sourceInstanceKey?: string
  /** The processor run that wrote it; absent for a component's own write. */
  actionId?: string
}

export interface ActionRunPayload {
  /** Unique within the page load. */
  actionId: string
  /** The schema event whose processors run (`onClick`, `onPageShow`, `onTimerZero`, …). */
  eventName: string
  /** The component whose event it is (`ref/…/nodeId`); empty for a page's own events. */
  sourceInstanceKey: string
  /** The run whose `reemit` started this one: a shared node's processors joining its trigger's. */
  parentActionId?: string
  /**
   * The DOM event the component handed its listener, when it handed one. A CTA acts on the
   * pointerup and cancels the touch's click, so this is the only trace of the press.
   */
  trigger?: Event
}

export interface ActionEndPayload {
  actionId: string
}

export interface NavigationRequestPayload {
  pageId: string
  /** The processor run that asked; absent for a direct call (a link in rich text). */
  actionId?: string
}

// Momentary signals that must NOT be replayed from `eventHistory` to a new
// subscriber: a past exit-intent / page-leave / network error is over and
// re-firing it when a component (e.g. a SharedNodeRefRenderer) mounts later
// would run that component's onExitIntent / onPageLeave for an event that
// already happened on an earlier page. `navigation:show` (and `error:track`)
// stay replayable so late subscribers genuinely catch up on current state.
const NON_REPLAYABLE_EVENTS = new Set([
  'navigation:exit-intent',
  'navigation:leave',
  PIPE_EVENTS.networkError,
  // An imperative command — replaying past ones to a late
  // subscriber would just re-run an idempotent no-op N times.
  PIPE_EVENTS.trackingRefresh,
  // Momentary display frames — replaying a finished countdown to a TipTap that
  // mounts later would overlay a stale value on the live one.
  PIPE_EVENTS.variableTick,
  // The current value is in the store; a replayed write would be a stale one.
  PIPE_EVENTS.variableSet,
  // A finished run or an old navigation request tells a late subscriber nothing.
  PIPE_EVENTS.actionStart,
  PIPE_EVENTS.actionEnd,
  PIPE_EVENTS.navigationRequest,
])

// Single source of truth for trackError context strings. Values are written
// to be human-readable in the Bugsnag dashboard title row. Adding a new
// context = an explicit edit here; the `ErrorContext` type below makes the
// `context` field on ErrorReport reject arbitrary strings at the call site.
// NOTE: the `prehydration*` and `runtime*` values are duplicated as string
// literals in the HEAD inline script (bugsnag.ts getHead) — the inline error /
// rejection / fetch / resource handlers emit the `[Pre-hydration] *` label
// before Bugsnag starts and the `[Runtime] *` label after. Keep both sides in
// sync.
export const ERROR_CONTEXTS = {
  pipeSubscribe: '[Pipe] Subscribe',
  pipeSubscribeReplay: '[Pipe] Subscribe Replay',
  pipeNavigationCancelled: '[Pipe] Navigation Cancelled',
  evalScript: '[Event Processor] Eval Script',
  eventProcessor: '[Event Processor]',
  pageLifecycle: '[Page] Lifecycle',
  pipeordersQueue: '[PipeOrders] Queue',
  pipeordersCreateOrder: '[PipeOrders] Create Order',
  pipeordersNavigateToApp: '[PipeOrders] Navigate to App',
  pipeordersDevWebhookReplay: '[PipeOrders] Dev Webhook Replay',
  pipeordersAcRestore: '[PipeOrders] AC Restore',
  geoDeviceFetch: '[GeoDevice] Fetch',
  primerCheckout: '[PrimerCheckout]',
  primerCheckoutLoadTimeout: '[PrimerCheckout] Confirmation Load Timeout',
  primerCheckoutAbandoned: '[PrimerCheckout] Abandoned',
  primerCheckoutPrereqWait: '[PrimerCheckout] Prereq Wait Timeout',
  yunoCheckout: '[YunoCheckout]',
  yunoCheckoutLoadTimeout: '[YunoCheckout] Confirmation Load Timeout',
  yunoCheckoutAbandoned: '[YunoCheckout] Abandoned',
  yunoCheckoutPrereqWait: '[YunoCheckout] Prereq Wait Timeout',
  ctaButtonClickHandler: '[CTAButton] onClick',
  rawIntegrationExecution: '[RawIntegration] Execute Event',
  prehydrationResource: '[Pre-hydration] Resource',
  prehydrationError: '[Pre-hydration] Error',
  prehydrationUnhandledRejection: '[Pre-hydration] Unhandled Rejection',
  prehydrationFetch: '[Pre-hydration] Fetch',
  runtimeResource: '[Runtime] Resource',
  runtimeError: '[Runtime] Error',
  runtimeUnhandledRejection: '[Runtime] Unhandled Rejection',
  runtimeFetch: '[Runtime] Fetch',
} as const

export type ErrorContext = (typeof ERROR_CONTEXTS)[keyof typeof ERROR_CONTEXTS]

// Reported via PIPE_EVENTS.errorTrack.
// Subscribers translate this into their own API.
// The `metadata` is free-form and bug tracker agnostic.
export interface ErrorReport {
  error: unknown
  context: ErrorContext
  metadata?: Record<string, unknown>
}

export interface ActiveExtensionDef {
  type: string
  key: string
  singleton: boolean
  blocking?: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  config: any
  module: PipeIntegration
}

export interface PipeEventRecord {
  eventName: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload: any
  timestamp: number
}

/**
 * Payload shape for page navigation events.
 * Broadcast via `pipe.emit('navigation:show' | 'navigation:leave', payload)`
 * and passed as the first arg to `onPageShow` / `onPageLeave` event processors.
 */
export interface NavigationEventPayload {
  pageId: string
  path: string
  title?: string
  timestamp: number
  // Only on `navigation:exit-intent` — which mechanic decided the visitor is
  // leaving. Mirrored to `var.global.exitIntentTrigger` by the page runtime.
  trigger?: ExitIntentTrigger
}

/**
 * Sentinel returned by an event processor's handler to halt the rest of the
 * processor chain for the current event invocation. Used by `submit_form`
 * to abort the chain when form validation fails. A typed Symbol (not `false`)
 * keeps the contract unambiguous and future-predicate-safe.
 */
export const HALT_PROCESSOR_CHAIN: unique symbol = Symbol('halt-processor-chain')

export interface PipeEventProcessorDef {
  type: string // e.g., 'ga4_event', 'raw_custom_handler'
  handler: (payload: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    args: any[]
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any
    pipe: PipeInterface
    /**
     * Instance key of the component that fired the event (composed path —
     * `[...sharedRefAncestors, nodeId].join('/')`). Used by the
     * `component_processor` stock handler to prefix-walk the instance registry.
     * Empty string when the source is page-level or outside a component.
     */
    sourceInstanceKey?: string
    /**
     * The run this processor is part of (`action:start`). There is no ambient "current
     * run" to fall back on — runs interleave across awaits — so a processor passes it on
     * with whatever it causes, or that effect cannot be tied to what set the run off.
     */
    actionId?: string
    // Return HALT_PROCESSOR_CHAIN to short-circuit the remainder of this
    // event's processor chain. See submit_form for the canonical use.
  }) => void | typeof HALT_PROCESSOR_CHAIN | Promise<void | typeof HALT_PROCESSOR_CHAIN>
}

/**
 * Handler signature for a processor a component exposes to other components.
 * Registered at runtime by `useComponentProcessors` during the component's
 * setup; looked up by the `component_processor` stock event processor.
 */
export type ComponentProcessorHandler = (payload: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  args: any[]
  config: Record<string, unknown>
  pipe: PipeInterface
}) => void | Promise<void>

/**
 * Sentinel key for the page-implicit form group — used when no `<Form>`
 * ancestor provides a group key. Stable string (not page-id-derived) since
 * Nuxt navigates by unmount+mount, so per-page entries are never live at
 * the same time.
 */
export const PAGE_FORM_GROUP_KEY = '__page__'

/**
 * Entry in a form-group's input registry. Registered by `useFormInput` from
 * each participating input component.
 */
export interface FormInputEntry {
  inputKey: string
  validate: () => boolean | Promise<boolean>
  markTouched: () => void
  // Bare variable keys (no `var.` prefix) this input writes transiently and
  // that should be promoted to the persisted store when THIS group submits.
  // Scopes the commit to the submitting group so a transient edit made on a
  // different step (e.g. via browser Back) is never persisted by an unrelated
  // submit elsewhere.
  persistKeys?: () => (string | null | undefined)[]
}

/**
 * Submit target for a form group — the component (today, CTAButton) whose
 * click is the form's primary submit gesture. Registered by the CTA on
 * mount when `doNotSubmitForm` is unset.
 */
export interface FormSubmitTarget {
  sourceKey: string
  submit: () => Promise<void>
}

/**
 * Editor-only command handler. Components register these via
 * `useEditorCommands` to let field-editor buttons imperatively poke at live
 * instance state (preview animation, force a tab switch, etc.) without
 * exposing the operation as a user-wireable processor. The editor posts
 * `EXTERNAL_EDITOR_COMMAND` into the preview iframe; the dispatcher looks up
 * the handler by `(nodeId, command)` and calls it. No SharedNodeRef ancestry
 * is threaded through — the field editor addresses nodes by raw nodeId.
 */
export type EditorCommandHandler = (payload: {
  config: Record<string, unknown>
}) => void | Promise<void>

// --- Types ---
export interface PipeInterface {
  // State
  variables: Ref<Record<string, unknown>>
  data: ShallowRef<PipeFullReleaseDto | null>
  translations: Ref<Record<string, object>>
  constants: Ref<Record<string, unknown>>
  isInitialized: Ref<boolean>
  activeExtensions: Ref<Map<string, ActiveExtensionDef>>
  disableAllVisibilityRules: Ref<boolean>
  // Editor-only per-node force show/hide overrides (keyed by nodeId). Empty in a
  // real build — only ever populated by the editor preview bridge.
  visibilityOverrides: Ref<Record<string, 'show' | 'hide'>>
  // Editor-only opt-in: lets node animations run in EDITOR mode, which they
  // otherwise never do (useNodeAnimation.ts). Off by default because the
  // authoring canvas remounts nodes constantly; the visibility-rule tester
  // turns it on so a rule flip can be watched the way a visitor sees it.
  editorAnimations: Ref<boolean>
  currentMode: Ref<PipeDisplayMode>
  setMode: (mode: PipeDisplayMode) => void

  // Editor-only: id of the currently selected component in the preview iframe.
  // Written by `useEditorEvents` on local click and on `EXTERNAL_ACTIVATE`
  // messages from the parent; cleared when leaving EDITOR mode. Components
  // that need to react to selection (e.g. Carousel pausing autoplay and
  // centring on a selected child) read this. `null` outside the editor.
  activeNodeId: Ref<string | null>

  getPageById: (id: string) => Page | null
  getSharedNodeById: (id: string) => {
    id: string
    title: string
    content: { children: unknown[] }
    events?: Record<string, unknown[]>
  } | null
  getData: (key: string) => unknown
  getTranslation: (key: string | undefined) => object | undefined

  // Append `pageId`/`path` to the user-variables `global.visitedPageIds` and
  // `global.visitedPagePaths` arrays (index-aligned, deduped by pageId — first
  // visit wins). Stored in `variables` so visibility rules, components and
  // integrations can read them via `getData('var.global.visitedPageIds')`.
  markPageVisited: (pageId: string, path: string) => void

  // Actions
  initialize: ({
    initialData,
    extensions,
    editorPipeId,
  }: {
    initialData?: PipeFullReleaseDto
    extensions?: ActiveExtensionDef[]
    editorPipeId?: string
  }) => Promise<void>
  registerExtensions: (extensions: ActiveExtensionDef[]) => void

  setData: (
    key: string,
    value: unknown,
    opts?: { persist?: boolean; sourceInstanceKey?: string; actionId?: string },
  ) => void
  setVariableSilent: (key: string, value: unknown) => void
  setTranslations: (translations: Record<string, object>, language?: string) => void
  patchTranslation: (key: string, content: object | null | undefined) => void
  setTranslation: (key: string, value: object) => void
  setConstants: (newConstants: Record<string, object>) => void
  setSharedNodes: (
    nodes: Array<{
      id: string
      title: string
      content: { children: unknown[] }
      events?: Record<string, unknown[]>
    }>,
  ) => void
  navigateToPageId: (
    id: string,
    newQueries?: Record<string, unknown>,
    opts?: { actionId?: string },
  ) => void
  armNavigationRetry: () => void

  getPageBySlug: (path: string) => Page | null

  events: EventTarget
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  emit: (eventName: string, payload?: any) => void
  eventHistory: PipeEventRecord[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  subscribe: (eventName: string, callback: (payload: any) => void) => () => void

  // Reports a caught error: console.error + emit on PIPE_EVENTS.errorTrack.
  // Any integration subscribed to that event (Bugsnag etc.) receives the
  // report; when nothing is subscribed, the console.error is the only effect.
  trackError: (report: ErrorReport) => void
  // Event processors/handlers
  getAvailableProcessors: () => Map<string, PipeEventProcessorDef>
  getEventProcessor: (type: string) => PipeEventProcessorDef | undefined

  // Component-provided processor registry (per-instance handles)
  registerComponentProcessors: (
    instanceKey: string,
    handlers: Record<string, ComponentProcessorHandler>,
  ) => void
  unregisterComponentProcessors: (instanceKey: string) => void
  getComponentProcessor: (
    instanceKey: string,
    processor: string,
  ) => ComponentProcessorHandler | undefined
  // Resolve a component_processor target against the instance registry (trying
  // candidateKeys longest-prefix-first) and invoke it; if no target is
  // registered yet, defer and retry when one mounts (with a TTL backstop).
  dispatchComponentProcessor: (dispatch: {
    candidateKeys: string[]
    processor: string
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    args: any[]
    config: Record<string, unknown>
    targetPath: string[]
    sourceInstanceKey: string
  }) => Promise<void>

  // Editor-only command registry. Keyed by raw nodeId (no ancestry composition
  // — the field editor only addresses concrete nodes). Separate from
  // component processors so editor commands don't pollute the user's
  // event-wiring dropdown.
  registerEditorCommands: (nodeId: string, handlers: Record<string, EditorCommandHandler>) => void
  unregisterEditorCommands: (
    nodeId: string,
    handlers?: Record<string, EditorCommandHandler>,
  ) => void
  getEditorCommand: (nodeId: string, command: string) => EditorCommandHandler | undefined

  // Form-group registry — populated by `useFormInput` (inputs) and the CTA
  // (submit target). Keyed by `formGroupKey` provided through Vue inject by
  // either a `<Form>` component or the page-implicit fallback in
  // `RenderComponents`. Used by the CTA's pre-click step and the
  // `submit_form` event processor to validate every input in scope.
  registerFormInput: (groupKey: string, entry: FormInputEntry) => void
  unregisterFormInput: (groupKey: string, inputKey: string) => void
  registerFormSubmitTarget: (groupKey: string, target: FormSubmitTarget) => void
  unregisterFormSubmitTarget: (groupKey: string, sourceKey: string) => void
  getFormSubmitTarget: (groupKey: string) => FormSubmitTarget | null
  // Marks every registered input touched, runs every validator, returns
  // true iff all pass. Always returns true in EDITOR mode (no submit).
  submitFormGroup: (groupKey: string) => Promise<boolean>
  // For the `submit_form` event processor — handler can't inject Vue
  // context, so submit targets register their source-instance-key → group-key
  // mapping here for the dispatcher to look up.
  getFormGroupForSource: (sourceInstanceKey: string) => string | undefined

  // Exit-intent suppression registry - token-based so multiple in-flight
  // suppressors (e.g. concurrent checkouts) compose without clobbering. The
  // exit-intent detector ([...slug].vue) consults isExitIntentSuppressed()
  // before firing; a non-empty set means "a component is mid-operation
  // (e.g. a wallet payment) and EI must not fire."
  suppressExitIntent: (token: string) => void
  releaseExitIntent: (token: string) => void
  isExitIntentSuppressed: () => boolean

  // Stored config of an active integration, by `IntegrationType`. Lets render
  // code that is NOT the integration (the page runtime reading EXIT_INTENT,
  // say) act on a setting without importing an optional integration module
  // into its bundle: absent integration → undefined → the caller's defaults.
  // Only meaningful after `initialize()` has registered the extensions, which
  // app.vue does before any page component sets up.
  //
  // Returns the first registered row of that type. Extensions are registered
  // pipe → brand → global, so that is the most specific scope's config — and
  // for a singleton the build has already dropped the rows it overrides.
  getIntegrationConfig: <T = Record<string, unknown>>(type: string) => T | undefined

  savePersistedData: () => void
  clearPersistedData: () => void
  // Promotes transient (persist:false) variable writes into the persisted
  // snapshot. Called on a valid form submit. With no args, commits every
  // pending key; pass keys to commit a subset.
  commitVariables: (keys?: string[]) => void

  // Resolves once both gates have cleared:
  //   1. Persisted-data load (localStorage→variables merge, deferred via
  //      setTimeout(0) for hydration safety).
  //   2. All `blocking: true` integrations have finished their onReady() work
  //      (e.g. PIPEORDERS restoring order vars from the API).
  // Components reading `var.global.*` from `onMounted` should always
  // `await pipe.whenReady()` first. Resolves immediately when called from
  // inside a blocking integration's own onReady() — the blocking gate isn't
  // set yet at that point, so there's no circular deadlock.
  whenReady: () => Promise<void>
}

export enum PipeDisplayMode {
  EDITOR = 'editor',
  VIEW = 'view',
  INTERACTIVE_VIEW = 'interactiveView',
  STATIC = 'static',
}

declare global {
  interface Window {
    pipe?: PipeInterface | null
    pipeMode?: 'editor' | 'view' | 'interactiveView' | 'live' | 'static'
  }
}

// --- Injection Symbol ---
const PipeSymbol: InjectionKey<PipeInterface> = Symbol('Pipe')

// --- Local Storage Helper ---
const isLocalStorageAvailable = (): boolean => {
  if (typeof window === 'undefined') return false
  try {
    const testKey = '__storage_test__'
    window.localStorage.setItem(testKey, testKey)
    window.localStorage.removeItem(testKey)
    return true
  } catch {
    console.warn('[Pipe] Local storage is not available. Data persistence will be disabled.')
    return false
  }
}

// --- Main Setup Function ---
export function setupPipeState() {
  // 1. Define State (Replaces the external Store)
  const storageKey = ref<string | null>(null)
  // The raw distribution path behind `storageKey` — kept separately because
  // the ingress trace-id record (@render/lib/ingressTrace) needs a storage
  // key of its own that survives `clearPersistedData`.
  const persistedDistributionPath = ref<string | null>(null)
  // Set to true once the deferred-load merge in `loadPersistedData` has run.
  // Gates `savePersistedData` so same-tick writes (e.g. `markPageVisited`
  // from the route page's onMounted) can't overwrite localStorage with the
  // pre-merge in-memory state and wipe the previous session's data.
  const persistedDataLoaded = ref(false)
  // Variable keys whose current in-memory value is a transient edit (e.g. an
  // input mid-typing) that must NOT be persisted to localStorage until an
  // explicit submit commits it. `savePersistedData` excludes these from the
  // serialized snapshot; `commitVariables` clears them so the next save flushes
  // them. See the "defer transient input persistence" design.
  const uncommittedKeys = new Set<string>()
  // Mirror of what is currently on disk, so excluded (uncommitted) keys keep
  // their last-persisted value across saves rather than being dropped.
  let lastPersisted: Record<string, unknown> = {}
  // Shallow: the release document is assigned once and never mutated after, so
  // deep reactivity would only add proxy cost to every node read on the render
  // path. A nested write will NOT re-render — reassign `.value` instead.
  const data = shallowRef<PipeFullReleaseDto | null>(null)
  // NOT shallow: `patchTranslation` writes single keys to drive the editor's
  // per-key delta channel.
  const translations = ref<Record<string, object>>({})
  // Initialize `system.persistedDataLoaded` synchronously so it's observable
  // identically on the server (during SSG/SSR prerender) and on the client's
  // first render, then flipped to `true` inside `loadPersistedData`'s
  // deferred timeout once the localStorage merge has run. Components that
  // read other persisted variables can `v-if="getData('var.system.persistedDataLoaded')"`
  // to render a skeleton on the falsy branch, avoiding hydration mismatches
  // and FOUC when persisted values land.
  const variables = ref<Record<string, unknown>>({
    'system.persistedDataLoaded': false,
  })
  // Per-variable default values resolved at release build (pipe default +
  // per-distribution override), keyed "group.name". Seeded into `variables`
  // only when not already persisted — see seedOriginDefaults.
  const releaseVariableDefaults = ref<Record<string, unknown>>({})
  const constants = ref<Record<string, unknown>>({})
  const events = new EventTarget()
  const eventHistory: PipeEventRecord[] = []
  const isInitialized = ref(false)
  const activeExtensions = ref<Map<string, ActiveExtensionDef>>(new Map())
  let blockingIntegrationsPromise: Promise<void> = Promise.resolve()
  const eventProcessors = ref<Map<string, PipeEventProcessorDef>>(
    new Map(Object.entries(STOCK_EVENT_PROCESSORS)),
  )
  const disableAllVisibilityRules = ref(false)
  const visibilityOverrides = ref<Record<string, 'show' | 'hide'>>({})
  const editorAnimations = ref(false)
  const sharedNodes = ref<
    Array<{
      id: string
      title: string
      content: { children: unknown[] }
      events?: Record<string, unknown[]>
    }>
  >([])

  const currentMode = ref<PipeDisplayMode>(
    (typeof window !== 'undefined' && (window.pipeMode as PipeDisplayMode)) ||
      PipeDisplayMode.EDITOR,
  )

  const activeNodeId = ref<string | null>(null)

  const setMode = (mode: PipeDisplayMode) => {
    currentMode.value = mode
    constants.value['system.pipeMode'] = mode
    if (typeof window !== 'undefined') window.pipeMode = mode
    if (typeof document !== 'undefined') {
      // Replace any existing mode-* class (previously they just accumulated)
      for (const cls of Array.from(document.body.classList)) {
        if (cls.startsWith('mode-')) document.body.classList.remove(cls)
      }
      document.body.classList.add(`mode-${mode}`)
    }
  }

  // Cache Nuxt's useRoute() at setup time to avoid calling it lazily during middleware
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let currentRoute: { query: Record<string, any> } | null = null
  try {
    // @ts-expect-error Nuxt auto-import — not available in non-Nuxt UI project
    currentRoute = useRoute()
  } catch {
    // Not in Nuxt context (e.g. main UI project)
  }

  const getPageById = (id: string): Page | null => {
    return data.value?.latestContent?.content.pages.find((p) => p.id === id) || null
  }

  const VISITED_IDS_KEY = 'global.visitedPageIds'
  const VISITED_PATHS_KEY = 'global.visitedPagePaths'
  const CURRENT_DAY_KEY = 'global.currentDay'
  const NEXT_MONTH_DATE_KEY = 'system.nextMonthDate'
  const MONTH_AFTER_NEXT_DATE_KEY = 'system.monthAfterNextDate'

  const markPageVisited = (pageId: string, path: string) => {
    const idsRaw = variables.value[VISITED_IDS_KEY]
    const pathsRaw = variables.value[VISITED_PATHS_KEY]
    const ids = Array.isArray(idsRaw) ? (idsRaw as string[]) : []
    const paths = Array.isArray(pathsRaw) ? (pathsRaw as string[]) : []
    if (ids.includes(pageId)) return
    // Normalize path so visibility rules / integrations comparing against a
    // canonical slug see the same form regardless of how the route was reached
    // (e.g. `/foo/`, `foo`, ` /foo ` all collapse to `foo`).
    const normalizedPath = path.trim().replace(/^\/+|\/+$/g, '')
    // Reassign rather than mutate-in-place so Vue picks up the change for any
    // computed/watcher reading the variables map (matches the seedOriginDefaults
    // pattern below — full assignment, then savePersistedData).
    variables.value[VISITED_IDS_KEY] = [...ids, pageId]
    variables.value[VISITED_PATHS_KEY] = [...paths, normalizedPath]
    savePersistedData()
  }

  const getSharedNodeById = (id: string) => {
    // Check local sharedNodes ref first (used by preview iframe), then fall back to full pipe data (used by builder runtime)
    return (
      sharedNodes.value.find((s) => s.id === id) ??
      (
        data.value?.latestContent?.content as
          | {
              sharedNodes?: {
                id: string
                title: string
                content: { children: unknown[] }
                events?: Record<string, unknown[]>
              }[]
            }
          | undefined
      )?.sharedNodes?.find((s) => s.id === id) ??
      null
    )
  }

  const setSharedNodes = (
    nodes: Array<{
      id: string
      title: string
      content: { children: unknown[] }
      events?: Record<string, unknown[]>
    }>,
  ) => {
    sharedNodes.value = nodes
  }

  const resolveDeepValue = (target: Record<string, unknown>, fullPath: string) => {
    // 1. Fast path: Direct match (e.g., path is exactly "global.some-pat.other")
    if (fullPath in target) return target[fullPath]
    // 2. Find the base key (we look for the longest match in case of overlapping keys)
    let matchedBaseKey = ''
    for (const key of Object.keys(target)) {
      if (fullPath.startsWith(`${key}.`) && key.length > matchedBaseKey.length) {
        matchedBaseKey = key
      }
    }

    if (!matchedBaseKey) return undefined
    // 3. Extract the remaining path (e.g., "some.else")
    const remainingPath = fullPath.slice(matchedBaseKey.length + 1)
    const pathParts = remainingPath.split('.')
    // 4. Traverse the nested object.
    // Accessing properties here registers Vue's reactive dependencies.
    let current = target[matchedBaseKey]
    for (const part of pathParts) {
      if (current === null || current === undefined || typeof current !== 'object') {
        return undefined
      }
      // @ts-expect-error Dynamic access - we assume the structure is correct based on the key naming convention
      current = current[part]
    }
    return current
  }

  const getData = (key: string) => {
    const [type, actualKey] = splitKey(key)
    if (!actualKey) return undefined
    switch (type) {
      case 'variable':
        return resolveDeepValue(variables.value, actualKey)
      case 'translation':
        return translations.value[actualKey]
      case 'constant':
        return resolveDeepValue(constants.value, actualKey)
      default:
        //console.warn(`[Pipe] Unknown key type for "${key}". Returning undefined.`)
        return undefined
    }
  }

  const splitKey = (key: string): ['variable' | 'translation' | 'constant' | 'unknown', string] => {
    if (key.startsWith('var.')) return ['variable', key.slice(4)]
    if (key.startsWith('const.')) return ['constant', key.slice(6)]
    if (key.startsWith('tr.')) return ['translation', key.slice(3)]
    return ['unknown', key]
  }

  const getTranslation = (key: string | undefined) => {
    return key ? translations.value[key] : undefined
  }

  const setData = (
    key: string,
    value: unknown,
    opts?: { persist?: boolean; sourceInstanceKey?: string; actionId?: string },
  ) => {
    const [type, actualKey] = splitKey(key)
    if (!actualKey) {
      console.warn(`[Pipe] Ignored: setData called with empty key ("${key}")`)
      return
    }
    switch (type) {
      case 'variable':
        variables.value[actualKey] = value
        // Transient write (persist:false): update the reactive store live so
        // visibility rules / CTA enable-state / interpolation keep reacting,
        // but defer the localStorage persist until `commitVariables` (submit).
        if (opts?.persist === false) {
          uncommittedKeys.add(actualKey)
        } else {
          // An explicit committing write supersedes any pending transient one.
          uncommittedKeys.delete(actualKey)
          savePersistedData()
        }
        // Broadcast to the editor parent (if running in an iframe under the
        // builder) so the visibility-rules tester form can reflect runtime
        // changes that originated from the preview itself (e.g. set_data
        // processors triggered by user clicks).
        if (typeof window !== 'undefined' && window.parent !== window) {
          window.parent.postMessage(
            { type: 'VARIABLE_CHANGED', payload: { key: actualKey, value } },
            '*',
          )
        }
        emit(PIPE_EVENTS.variableSet, {
          key: actualKey,
          value,
          sourceInstanceKey: opts?.sourceInstanceKey || undefined,
          actionId: opts?.actionId,
        } satisfies VariableSetPayload)
        break
      case 'translation':
        console.warn(`[Pipe] Attempting to set translation "${key}" via setData.`)
        break
      case 'constant':
        console.warn(
          `[Pipe] Attempting to set constant "${key}" via setData. Constants are read-only.`,
        )
        break
      default:
        console.warn(`[Pipe] Unknown key type for "${key}" when setting Data. Data not set.`)
    }
  }

  // Silent variant for editor-pushed updates: applies the value without
  // re-broadcasting to the parent. Without this, an UPDATE_VARIABLES message
  // from the editor would call setData → broadcast VARIABLE_CHANGED → parent
  // re-broadcasts UPDATE_VARIABLES across all preview iframes → infinite
  // ping-pong (visible in pipe-flow with multiple previews).
  const setVariableSilent = (key: string, value: unknown) => {
    const [type, actualKey] = splitKey(key)
    if (type !== 'variable' || !actualKey) return
    variables.value[actualKey] = value
    // An explicit editor push commits the key (drops any pending transient state).
    uncommittedKeys.delete(actualKey)
    savePersistedData()
  }

  const setTranslations = (newTranslations: Record<string, object>, language?: string) => {
    translations.value = newTranslations
    if (language) {
      // The editor only knows the language code (the postMessage payload never
      // carries the DB rtl flag), so derive direction from the code here. Keep
      // both exposure points in sync for a live language switch in the preview.
      const rtl = isRtlLanguage(language)
      // Reassign `constants.value` (rather than mutating keys in place) so the
      // change reliably triggers downstream computeds — `useLocale().language`
      // and `useDirection()` only re-evaluate on a tracked update, and this
      // matches the reassignment pattern `initialize` uses in the build.
      constants.value = {
        ...constants.value,
        'system.language': language,
        'system.isRtl': rtl,
      }
      variables.value = { ...variables.value, 'global.isRtl': rtl }
    }
  }

  const setConstants = (newConstants: Record<string, object>) => {
    constants.value = { ...constants.value, ...newConstants }
  }

  // Single-key patch from the editor's translation delta channel. Mutates the
  // map in place (instead of replacing the ref like setTranslations) so only
  // consumers tracking this key re-run — a whole-map replace would re-render
  // every translated component on each edit.
  const patchTranslation = (key: string, content: object | null | undefined) => {
    if (!key) return
    if (content === null || content === undefined) {
      delete translations.value[key]
    } else {
      translations.value[key] = content
    }
  }

  const setTranslation = (key: string | undefined, value: object) => {
    if (typeof window === 'undefined' || !key) return
    // Guard against TipTap normalization echoes. When the editor pushes an
    // UPDATE_TRANSLATIONS snapshot, each translatable component (HeadingText,
    // CTAButton, TipTap-wrapped fields, …) receives the new modelValue through
    // a writable-computed setter that funnels here. If TipTap's onUpdate
    // produces a structurally-identical-but-not-reference-equal JSON, posting
    // it back to the parent feeds the bridge an "edit" it didn't make, which
    // bounces back into modifications and re-broadcasts to every preview in
    // pipe-flow. Bail if the incoming value already matches what we hold for
    // this key — only genuine user edits change structure.
    const current = translations.value[key]
    if (current !== undefined && JSON.stringify(current) === JSON.stringify(value)) return
    // Tag the message with the language the iframe is currently rendering.
    // Without this, the parent's UPDATE_TRANSLATION handler uses its own
    // `activeLanguage.value` — which can have moved on if the user is
    // cycling languages quickly across many previews — and a TipTap echo
    // produced while rendering language B can end up written to
    // `modifications[A][key]` when the parent has already switched to A.
    // `system.language` is set in `setTranslations` alongside the bulk
    // translations apply, so it always reflects the iframe's current view.
    const language = constants.value['system.language'] as string | undefined
    window.parent.postMessage(
      { type: 'UPDATE_TRANSLATION', payload: { key, value, language } },
      '*',
    )
  }

  // How long to let a competing navigation settle before re-pushing. The navigation that
  // cancelled ours is still running when we are told, and a same-document history change
  // resolves within a frame — so this is a wide margin that stays imperceptible, and it is
  // only ever waited out on a navigation that already failed.
  const NAVIGATION_RETRY_DELAY_MS = 150

  // Opts the NEXT navigation into the cancel-retry below, and nothing else. Armed only by
  // YunoCheckout, immediately before it hands the completed payment to the author's
  // navigation — so the retry can never touch an ordinary page-to-page click, where a
  // cancelled navigation is usually a second navigation that should be allowed to win.
  // Consumed on the next navigation whether or not it was the intended one, so a checkout
  // whose author wired no navigation cannot leave it armed indefinitely.
  let retryNextNavigationOnCancel = false
  const armNavigationRetry = () => {
    retryNextNavigationOnCancel = true
  }

  // Superseded by a newer navigation. Deliberately narrower than "did not land": `aborted`
  // means a navigation guard refused, and `duplicated` means we are already on the target.
  const wasCancelled = (result: unknown): result is NavigationFailure =>
    isNavigationFailure(result, NavigationFailureType.cancelled)

  const navigateToPageId = async (
    id: string,
    newQueries?: Record<string, unknown>, // Query params
    opts?: { actionId?: string },
  ) => {
    // Consumed unconditionally, before any early return, so one armed navigation can only
    // ever opt in one navigation.
    const retryOnCancel = retryNextNavigationOnCancel
    retryNextNavigationOnCancel = false

    // Editor preview: there is no Nuxt router inside the iframe, and
    // `data.value` is undefined in this context (the editor never feeds
    // `initialData`). Bubble the pageId up to the editor parent so it can
    // route to the matching page-edit view (preserving the active sub-mode:
    // editor / theme / translations / visibility-rules / json). Done BEFORE
    // the `data.value` guard so the editor case isn't lost to that warning.
    if (typeof window !== 'undefined' && window.parent !== window) {
      window.parent.postMessage({ type: 'NAVIGATE_TO_PAGE', payload: { pageId: id } }, '*')
      return
    }

    if (!data.value) return console.warn('[Pipe] No data available for navigation')

    const page = getPageById(id)

    // Nuxt specific router handling
    try {
      if (page?.path) {
        const cleanPath = `/${page.path}/`.replace(/\/{2,}/g, '/')

        // Merge old queries with the new ones.
        // If 'newQueries' has a key that already exists, it overrides the old one.
        const mergedQuery = {
          ...(currentRoute?.query || {}),
          ...(newQueries || {}),
        }
        const target = { path: cleanPath, query: mergedQuery }
        emit(PIPE_EVENTS.navigationRequest, {
          pageId: id,
          actionId: opts?.actionId,
        } satisfies NavigationRequestPayload)

        // A CANCELLED navigation resolves, it does not throw: Vue Router reports it as a
        // NavigationFailure return value and `navigateTo` passes that straight through, so
        // the catch below can never see it. Discarding the resolved value makes a lost
        // navigation completely silent — the visitor simply stays on the page, which is
        // indistinguishable from the page never being asked to move.
        //
        // That is only worth acting on for a payment that already succeeded, which is why
        // this is armed per-navigation rather than always on (see armNavigationRetry). The
        // trigger is the Yuno SDK calling `history.back()` to drop the history entry it
        // added for a 3DS challenge: the popstate cancels our push to the success page and
        // a customer who has PAID is left sitting on the checkout.
        //
        // Only `cancelled` is retried — see wasCancelled for why the other two are not.
        // @ts-expect-error Nuxt auto-import
        const failure = await navigateTo(target)
        if (retryOnCancel && wasCancelled(failure)) {
          trackError({
            error: new Error(`Navigation to ${cleanPath} was cancelled — retrying`),
            context: ERROR_CONTEXTS.pipeNavigationCancelled,
            metadata: { pageId: id, path: cleanPath, failureType: failure.type },
          })

          await new Promise((resolve) => setTimeout(resolve, NAVIGATION_RETRY_DELAY_MS))

          // @ts-expect-error Nuxt auto-import
          const retryFailure = await navigateTo(target)
          if (wasCancelled(retryFailure)) {
            trackError({
              error: new Error(`Navigation to ${cleanPath} failed again after retry`),
              context: ERROR_CONTEXTS.pipeNavigationCancelled,
              metadata: { pageId: id, path: cleanPath, failureType: retryFailure.type },
            })
          }
        }
      }
    } catch (e) {
      console.error('[Pipe] Router not available', e)
    }
  }

  const getPageBySlug = (path: string): Page | null => {
    const content = data.value?.latestContent?.content
    if (!content) return null
    const pages = content.pages ?? []
    let page = pages.find(
      (p: Page) => `/${p.path}` === path || p.path === path || `/${p.path}/` === path,
    )
    if (!page && path === '/' && pages.length > 0) {
      const landingId = content.landingPageId
      page = (landingId && pages.find((p) => p.id === landingId)) || pages[0]
    }
    return page || null
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const emit = (eventName: string, payload?: any) => {
    if (typeof window !== 'undefined') {
      // Kept only for replay, so a momentary signal (one per keystroke, for
      // `variable:set`) never accumulates in it.
      if (!NON_REPLAYABLE_EVENTS.has(eventName)) {
        eventHistory.push({
          eventName,
          payload,
          timestamp: Date.now(),
        })
      }

      // Broadcast live
      const event = new CustomEvent(eventName, { detail: payload })
      events.dispatchEvent(event)
    }
  }

  // Console-logs + emits on error:track for subscribed trackers. Late
  // subscribers catch up via subscribe()'s eventHistory replay. The `tracking`
  // flag breaks re-entry if a subscriber throws back into trackError.
  let tracking = false
  const trackError = (report: ErrorReport) => {
    if (tracking) {
      console.error('[Pipehouse] trackError re-entered', report.context, report.error)
      return
    }
    tracking = true
    try {
      console.error(`[Pipehouse] Context: ${report.context}`, report.error)
      emit(PIPE_EVENTS.errorTrack, report)
    } finally {
      tracking = false
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const subscribe = (eventName: string, callback: (payload: any) => void): (() => void) => {
    if (typeof window === 'undefined') return () => {}
    // Replay historical events so late subscribers catch up — except for
    // momentary signals (see NON_REPLAYABLE_EVENTS), which would otherwise
    // re-fire a past event when a new subscriber mounts. Each call is
    // try/catch'd so one bad subscriber doesn't skip the rest or escape as an
    // unhandled rejection.
    if (!NON_REPLAYABLE_EVENTS.has(eventName)) {
      const pastEvents = eventHistory.filter((e) => e.eventName === eventName)
      pastEvents.forEach((pastEvent) => {
        try {
          callback(pastEvent.payload)
        } catch (err) {
          trackError({
            error: err,
            context: ERROR_CONTEXTS.pipeSubscribeReplay,
            metadata: { eventName },
          })
        }
      })
    }
    const eventWrapper = (e: Event) => {
      try {
        callback((e as CustomEvent).detail)
      } catch (err) {
        trackError({
          error: err,
          context: ERROR_CONTEXTS.pipeSubscribe,
          metadata: { eventName },
        })
      }
    }
    // Live event listener
    events.addEventListener(eventName, eventWrapper)
    return () => {
      events.removeEventListener(eventName, eventWrapper)
    }
  }

  // Cached promise so every component awaiting `pipe.whenReady()` shares the
  // same resolution. Invalidated once after initialize() sets the real
  // blockingIntegrationsPromise so the next call re-combines both gates.
  let readyPromise: Promise<void> | null = null

  const buildReadyPromise = (): Promise<void> => {
    // SSR has no localStorage merge to wait for — `loadPersistedData` no-ops
    // out before the setTimeout — so resolve immediately and let the client's
    // post-hydration onMounted callers drive the actual gating.
    if (typeof window === 'undefined') return Promise.resolve()

    // The flag has three observable states:
    //   `false`     — initial, set in `setupPipeState`'s variables init.
    //                 Means the boot-time merge hasn't run yet → real wait.
    //   `true`      — merge has run. Safe to read persisted vars.
    //   `undefined` — variables wiped by something other than the documented
    //                 `clearPersistedData` (which now preserves the flag).
    //                 Treat as "no merge pending" so we don't deadlock.
    const persistedGate =
      variables.value['system.persistedDataLoaded'] !== false
        ? Promise.resolve()
        : new Promise<void>((resolve) => {
            const stop = watch(
              () => variables.value['system.persistedDataLoaded'],
              (loaded) => {
                if (loaded !== false) {
                  stop()
                  resolve()
                }
              },
              { flush: 'sync' },
            )
          })

    // Combine the localStorage gate with the blocking-integrations gate.
    // blockingIntegrationsPromise is Promise.resolve() until initialize() sets
    // it after firing all onReady() calls — so callers inside an integration's
    // own onReady() (e.g. PIPEORDERS awaiting whenReady() to read persisted
    // vars) never see the real blocking gate and cannot deadlock on themselves.
    return Promise.all([persistedGate, blockingIntegrationsPromise]).then(() => {})
  }

  const whenReady = (): Promise<void> => {
    if (readyPromise) return readyPromise
    readyPromise = buildReadyPromise()
    return readyPromise
  }

  const savePersistedData = () => {
    if (storageKey.value === null || !isLocalStorageAvailable()) return
    // Gate saves until the deferred load merge has completed. Without this
    // guard, any same-tick `setData` / `markPageVisited` (notably the route
    // page's `onMounted` firing `pipe.markPageVisited`) would overwrite
    // localStorage with the almost-empty pre-merge `variables.value`, wiping
    // the previous session's persisted state from disk. The setTimeout(0)
    // merge inside `loadPersistedData` itself is intentional and stays — it
    // prevents Vue hydration mismatches in the SSG-built pipe (server has no
    // localStorage, so the SSR'd HTML is rendered with empty vars; the
    // client's first render must match before reactive updates can mutate).
    if (!persistedDataLoaded.value) return
    // Build the snapshot excluding uncommitted (transient) keys. For each such
    // key, retain its last-persisted value if one exists; otherwise omit it so
    // an unsubmitted edit never reaches disk. This is what protects previously
    // saved data when an unrelated write (selection, markPageVisited, etc.)
    // triggers a save while a typed input is still mid-edit.
    // Always a fresh clone — never the live `variables.value` reference, so a
    // later transient write can't leak into `lastPersisted` and defeat the
    // exclusion on a subsequent save.
    const snapshot: Record<string, unknown> = { ...variables.value }
    for (const k of uncommittedKeys) {
      if (k in lastPersisted) snapshot[k] = lastPersisted[k]
      else delete snapshot[k]
    }
    try {
      window.localStorage.setItem(storageKey.value, JSON.stringify(snapshot))
      lastPersisted = snapshot
    } catch (e) {
      console.warn('[Pipe] Failed to save variables to local storage', e)
    }
  }

  // Promotes transient (persist:false) variable writes into the persisted
  // snapshot — called on a valid form submit. With no args, commits every
  // pending key; pass keys to commit a subset.
  const commitVariables = (keys?: string[]) => {
    if (keys) keys.forEach((k) => uncommittedKeys.delete(k))
    else uncommittedKeys.clear()
    savePersistedData()
  }

  // Seeds entry-point URL variables on first visit. Must run AFTER persisted
  // data is merged so a previously-captured origin survives subsequent reloads.
  const seedOriginDefaults = () => {
    if (typeof window === 'undefined') return
    let changed = false
    // srcdoc iframes (editor + pipe-flow preview) report origin === 'null',
    // which `new URL()` rejects — and the path is meaningless there anyway.
    const hasRealOrigin = window.location.origin && window.location.origin !== 'null'
    if (hasRealOrigin && variables.value['system.originUrl'] === undefined) {
      variables.value['system.originUrl'] = window.location.href
      changed = true
    }
    if (variables.value['system.originQueryString'] === undefined) {
      variables.value['system.originQueryString'] = window.location.search
      changed = true
    }
    // Ingress AB-test trace ids — origin = first landing's id (seed-once),
    // current = most recent landing's id; a URL without the param keeps both.
    // The dedicated per-distribution localStorage record is the source of
    // truth because it must survive the landing-page reset
    // (`clearIfLandingPage` → `clearPersistedData` wipes `pipe_store_*` — and
    // an ingress redirect lands ON the landing page); the two system vars are
    // mirrors force-re-asserted from it on every load, like the cookie-consent
    // flag above. See @render/lib/ingressTrace.
    const storedIngress = readStoredIngressTraceIds(persistedDistributionPath.value)
    const ingress = resolveIngressTraceIds(
      storedIngress,
      readUrlIngressTraceId(window.location.search),
    )
    if (ingress.origin !== storedIngress.origin || ingress.current !== storedIngress.current) {
      writeStoredIngressTraceIds(persistedDistributionPath.value, ingress)
    }
    if (variables.value[ORIGIN_INGRESS_TRACE_ID_VAR_KEY] !== ingress.origin) {
      variables.value[ORIGIN_INGRESS_TRACE_ID_VAR_KEY] = ingress.origin
      changed = true
    }
    if (variables.value[CURRENT_INGRESS_TRACE_ID_VAR_KEY] !== ingress.current) {
      variables.value[CURRENT_INGRESS_TRACE_ID_VAR_KEY] = ingress.current
      changed = true
    }
    if (variables.value[VISITED_IDS_KEY] === undefined) {
      variables.value[VISITED_IDS_KEY] = []
      changed = true
    }
    if (variables.value[VISITED_PATHS_KEY] === undefined) {
      variables.value[VISITED_PATHS_KEY] = []
      changed = true
    }
    const today = new Date().toISOString().slice(0, 10)
    if (variables.value[CURRENT_DAY_KEY] !== today) {
      variables.value[CURRENT_DAY_KEY] = today
      changed = true
    }
    // Calendar-month math (not "+30 days"), via the LOCAL date so a visitor
    // near midnight never lands a day into the wrong month — see
    // formatLocalIsoDate's doc. `Date`'s month overflow normalizes into the
    // next year, so this rolls over Dec → Jan with no special-casing.
    const now = new Date()
    const nextMonthDate = formatLocalIsoDate(new Date(now.getFullYear(), now.getMonth() + 1, 1))
    if (nextMonthDate && variables.value[NEXT_MONTH_DATE_KEY] !== nextMonthDate) {
      variables.value[NEXT_MONTH_DATE_KEY] = nextMonthDate
      changed = true
    }
    const monthAfterNextDate = formatLocalIsoDate(
      new Date(now.getFullYear(), now.getMonth() + 2, 1),
    )
    if (monthAfterNextDate && variables.value[MONTH_AFTER_NEXT_DATE_KEY] !== monthAfterNextDate) {
      variables.value[MONTH_AFTER_NEXT_DATE_KEY] = monthAfterNextDate
      changed = true
    }
    // Re-assert the derived RTL mirror against the authoritative system.isRtl
    // constant. Runs after the localStorage merge (and on clearPersistedData),
    // so a stale persisted `global.isRtl` can never win.
    const isRtlValue = constants.value['system.isRtl'] === true
    if (variables.value['global.isRtl'] !== isRtlValue) {
      variables.value['global.isRtl'] = isRtlValue
      changed = true
    }
    // Re-assert the "is consent pending?" boolean from the dedicated consent
    // localStorage key (the source of truth, OUTSIDE pipe_store_*). Runs after
    // the localStorage merge AND inside clearPersistedData, so the visitor's
    // decision survives the landing-page reset — clearPersistedData never
    // touches that key. Missing/garbage → undecided → true (banner shows). See
    // @render/lib/cookieConsent.
    //
    // Per-distribution feature flag: cookie consent is OFF unless
    // `const.global.cookieConsentEnabled` is explicitly true. When off, force
    // not-pending so the banner's positive rule evaluates hidden. It is a
    // DEPLOYMENT gate, so the editor preview (EDITOR mode) always behaves as
    // enabled and the flag only bites in real builds (STATIC/LIVE). Absent →
    // disabled (un-seeded envs / legacy releases stay safe — tags fire, no banner).
    const consentEnabled =
      currentMode.value === PipeDisplayMode.EDITOR ||
      constants.value['global.cookieConsentEnabled'] === true
    const pending = consentEnabled && isConsentPending(readStoredConsent())
    if (variables.value[COOKIE_CONSENT_PENDING_VAR_KEY] !== pending) {
      variables.value[COOKIE_CONSENT_PENDING_VAR_KEY] = pending
      changed = true
    }
    // Seed release-baked per-variable defaults (pipe default + distribution
    // override) ONLY when nothing is already set. Because this runs after the
    // localStorage merge, a visitor's persisted choice (e.g. a measurement
    // toggle) is already present and wins; first visit / post-clear seeds the
    // configured default. Deliberately seed-if-not-set, unlike isRtl above.
    for (const [key, value] of Object.entries(releaseVariableDefaults.value)) {
      if (variables.value[key] === undefined) {
        variables.value[key] = value
        changed = true
      }
    }
    if (changed) savePersistedData()
  }

  const clearPersistedData = () => {
    if (storageKey.value === null || !isLocalStorageAvailable()) return
    try {
      window.localStorage.removeItem(storageKey.value)
      // Drop any pending transient edits and the on-disk mirror — there's
      // nothing persisted left to retain.
      uncommittedKeys.clear()
      lastPersisted = {}
      // Clear the in-memory variables too. Preserve `system.persistedDataLoaded`
      // (now `true` — there's nothing left to load) so components gating on
      // `v-if="getData('var.system.persistedDataLoaded')"` keep rendering, and
      // async waiters (e.g. usePrimer) don't deadlock waiting for a flag that
      // would never re-flip — `loadPersistedData` only runs once at app init.
      variables.value = { 'system.persistedDataLoaded': true }
      // Re-seed `system.originUrl`, `system.originQueryString`, and the
      // visited-page lists. These are only seeded inside the boot-time
      // `loadPersistedData` setTimeout, so without this call they stay
      // undefined for the rest of the SPA session — and downstream payloads
      // (e.g. `buildOriginUrl()` in usePaymentData) end up posting an empty
      // origin, which payments-api rejects.
      seedOriginDefaults()
    } catch (e) {
      console.warn('[Pipe] Failed to clear variables from local storage', e)
    }
  }

  // The merge runs inside `setTimeout(0)` so the first client render (during
  // Vue hydration of SSG/SSR'd HTML) sees the same `variables.value` the
  // server saw — localStorage isn't available on the server, so merging
  // synchronously here would diverge the client's first render and emit
  // hydration mismatch warnings. The flags are flipped inside the timeout:
  // - `persistedDataLoaded` (internal): gates `savePersistedData` so any
  //   same-tick writes (e.g. `markPageVisited` from the route page's
  //   onMounted) don't overwrite localStorage with the pre-merge state and
  //   wipe the previous session's data.
  // - `var.system.persistedDataLoaded` (component-visible): exposed to
  //   templates and visibility rules so authors can render skeletons until
  //   persisted state is in place.
  const loadPersistedData = (distributionPath: string) => {
    if (distributionPath === null) return
    persistedDistributionPath.value = distributionPath
    storageKey.value = `pipe_store_${distributionPath}`

    // SSR has no `window`; `savePersistedData` no-ops anyway and flipping
    // the system var here would diverge from the client's first render.
    // The client picks this up after hydration via the timeout below.
    if (typeof window === 'undefined') return

    setTimeout(() => {
      let didLoadFromStorage = false
      if (isLocalStorageAvailable()) {
        try {
          const storedVars = window.localStorage.getItem(storageKey.value!)
          if (storedVars) {
            const parsed = JSON.parse(storedVars)
            variables.value = { ...variables.value, ...parsed }
            // Seed the on-disk mirror so excluded (uncommitted) keys retain
            // their persisted value on subsequent saves.
            lastPersisted = { ...parsed }
            didLoadFromStorage = true
          }
        } catch (e) {
          console.warn('[Pipe] Failed to parse locally stored variables.', e)
        }
      }
      // Flip both flags BEFORE seedOriginDefaults so the seed's optional
      // `savePersistedData` call (when any system.* default is freshly
      // assigned) is no longer gated.
      variables.value['system.persistedDataLoaded'] = true
      persistedDataLoaded.value = true
      seedOriginDefaults()
      if (didLoadFromStorage) {
        activeExtensions.value.forEach((ext) => {
          if (typeof ext.module === 'object' && ext.module.onDataLoaded) {
            ext.module.onDataLoaded(pipeInterface)
          }
        })
      }
    }, 0)
  }

  const initialize = async ({
    initialData,
    extensions,
    editorPipeId,
  }: {
    initialData?: PipeFullReleaseDto
    extensions?: ActiveExtensionDef[]
    editorPipeId?: string
  }) => {
    // In editor initialData is not available; persisted variables are keyed
    // by pipe id so each pipe in the editor has its own store (mirroring the
    // distribution-path-keyed prod store). Without a pipe id we skip loading
    // rather than fall back to a shared key.
    if (!initialData) {
      if (editorPipeId) {
        loadPersistedData(`editor_${editorPipeId}`)
        // Editor preview has no built bundle, so distributionId / buildId are
        // unavailable — seed sentinel placeholders so downstream code (and any
        // accidental order events) can tell editor traffic apart from real
        // builds. pipeId is known from the route.
        constants.value = {
          ...constants.value,
          'system.pipeId': editorPipeId,
          'system.distributionId': '__editor__',
          'system.buildId': '__editor__',
          // No release language yet; setTranslations() updates system.language
          // (which drives direction via useDirection) and system.isRtl live when
          // the editor pushes the active translation language. Seed false so
          // var.global.isRtl has a defined value before the first switch.
          'system.isRtl': false,
        }
      } else {
        console.warn(
          '[Pipe] initialize() called without initialData or editorPipeId — skipping persisted-variable load',
        )
      }
      return
    }

    // Normalise Data
    if (initialData.latestContent?.content?.pages) {
      initialData.latestContent.content.pages.forEach((page: Page) => {
        if (page.path.startsWith('/')) page.path = page.path.slice(1)
        if (!page.path.endsWith('/')) page.path += '/'
      })
    }

    // Update State
    // markRaw so a downstream `reactive()` / deep `ref()` can't re-proxy it.
    data.value = markRaw(initialData)
    if (initialData.translation) {
      translations.value = initialData.translation
    }

    // Right-to-left for this release. The backend derives this from the
    // language code (RTL_LANGUAGE_CODES) and carries it as `initialData.isRtl`;
    // fall back to deriving from the language code here too, so older releases
    // (built before RTL support) and any path that omits the flag still resolve
    // correctly.
    const isRtlValue =
      typeof initialData.isRtl === 'boolean'
        ? initialData.isRtl
        : isRtlLanguage(initialData.language)

    // Set constants
    constants.value = {
      ...constants.value,
      ...initialData.constants,
      'system.language': initialData.language,
      'system.isRtl': isRtlValue,
      'system.geography': initialData.geography,
      'system.currency': initialData.currency ?? 'usd',
      'system.distributionName': initialData.distributionName,
      'system.distributionPath': initialData.distributionPath,
      'system.brandCode': initialData.brand?.code ?? '',
      'system.pipeMode': currentMode.value,
      'system.pipeId': initialData.id ?? null,
      'system.distributionId': initialData.distributionId ?? null,
      'system.buildId': initialData.buildId ?? null,
      // Resolved (single-currency) locale override baked into the release.
      // `useLocale` reads it via `const.system.localeConfig` and merges it over
      // the Intl-derived defaults. Empty object when no override exists.
      'system.localeConfig': ((initialData as { localeConfig?: object }).localeConfig ??
        {}) as object,
    }

    // Author-facing mirror of system.isRtl, for visibility rules and
    // interpolation. Set synchronously so SSR/prerender and the first client
    // render agree (re-asserted after the localStorage merge in
    // seedOriginDefaults so a stale persisted value can't win).
    variables.value['global.isRtl'] = isRtlValue

    // Stash the release-baked per-variable defaults; they are RE-applied (only
    // when not already persisted) inside seedOriginDefaults, which runs after
    // the localStorage merge so a visitor's saved choice wins on reload.
    releaseVariableDefaults.value =
      (initialData as { variables?: Record<string, unknown> }).variables ?? {}

    // Keys seedOriginDefaults force-re-asserts from a runtime source. Both are
    // `global.`-scoped, so a same-named authored variable could legitimately
    // carry a release default — and a build-time value for either can never be
    // right. Unlike every other default it would additionally be baked into the
    // prerendered HTML and then corrected one tick after hydration.
    // `global.isRtl` needs no entry: it is assigned just above, so the
    // seed-if-undefined test below already skips it.
    const nonPrerenderableDefaultKeys = new Set<string>([
      CURRENT_DAY_KEY,
      COOKIE_CONSENT_PENDING_VAR_KEY,
    ])

    // Seed the defaults SYNCHRONOUSLY so they are observable during SSR /
    // `nuxt generate` prerender. seedOriginDefaults runs inside
    // loadPersistedData's setTimeout(0) and bails out entirely on the server,
    // so without this every var-dependent visibility rule prerenders false and
    // its node is missing from the static HTML (an SEO hole). Seed-if-undefined
    // keeps the precedence order intact: the localStorage merge below still
    // overwrites with a visitor's persisted choice, and an AC order restore
    // (which lands after that merge) still wins over both.
    //
    // The loop in seedOriginDefaults STAYS — it is what re-seeds the defaults
    // after clearPersistedData() wipes variables.value on a landing-page reset.
    for (const [key, value] of Object.entries(releaseVariableDefaults.value)) {
      if (nonPrerenderableDefaultKeys.has(key)) continue
      if (variables.value[key] === undefined) variables.value[key] = value
    }

    loadPersistedData(initialData.distributionPath)

    if (extensions) {
      registerExtensions(extensions)
    }

    isInitialized.value = true

    const blockingPromises: Promise<void>[] = []
    activeExtensions.value.forEach((ext) => {
      if (typeof ext.module === 'object' && ext.module.onReady) {
        const p = ext.module.onReady(pipeInterface, ext.config) as Promise<void> | undefined
        if (ext.blocking && p) blockingPromises.push(p)
      }
    })
    blockingIntegrationsPromise = blockingPromises.length
      ? Promise.all(blockingPromises).then(() => {})
      : Promise.resolve()
    // Invalidate the cache so the next whenReady() call re-combines the
    // localStorage gate with the now-real blockingIntegrationsPromise.
    readyPromise = null

    // Helper event for editors to know when the pipe config is loaded and ready
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('pipe-config-loaded', { detail: pipeInterface }))
    }
  }

  const registerExtensions = (extensions: ActiveExtensionDef[]) => {
    extensions.forEach((ext) => {
      const extKey = ext.singleton ? ext.type : `${ext.type}:${ext.key}`
      if (activeExtensions.value.has(extKey)) {
        console.warn(`[Pipe] Integration "${extKey}" is already registered. Skipping.`)
        return
      }

      const integrationInstance = typeof ext.module === 'function' ? ext.module() : ext.module
      // Overwrite the definition's module with the instantiated object
      // so the Pipe state holds the working instance with its private memory.
      ext.module = integrationInstance
      // Copy blocking flag from the resolved module onto the ext record so
      // initialize()'s onReady loop can read ext.blocking reliably.
      if (integrationInstance.blocking) ext.blocking = true
      activeExtensions.value.set(extKey, ext)

      // Register Custom Processors
      if (integrationInstance.getEventProcessors) {
        const customProcessors = integrationInstance.getEventProcessors(ext.config)
        customProcessors.forEach((proc) => {
          // Name collision detection!
          if (eventProcessors.value.has(proc.type)) {
            console.error(
              `[Pipe] Collision: Event processor "${proc.type}" is already registered. Integrations must use unique names (e.g., '${ext.type}_${proc.type}').`,
            )
            return
          }
          eventProcessors.value.set(proc.type, proc)
        })
      }

      if (integrationInstance.setup) {
        integrationInstance.setup(pipeInterface, ext.config)
      }
      // If the integration is registered AFTER the pipe is already initialized
      // (e.g., dynamically loaded in the iframe), trigger onReady immediately.
      if (isInitialized.value && integrationInstance.onReady) {
        integrationInstance.onReady(pipeInterface, ext.config)
      }
    })
  }
  const getAvailableProcessors = () => eventProcessors.value
  const getEventProcessor = (type: string) => eventProcessors.value.get(type)

  // Component-provided processors — instance-keyed handle registry populated
  // on mount by `useComponentProcessors` in each component. `instanceKey` is
  // `[...sharedNodeRefAncestors, nodeId].join('/')` so the same template node
  // rendered via multiple SharedNodeRefs registers under distinct keys.
  const componentProcessors = new Map<string, Record<string, ComponentProcessorHandler>>()

  const registerComponentProcessors = (
    instanceKey: string,
    handlers: Record<string, ComponentProcessorHandler>,
  ) => {
    componentProcessors.set(instanceKey, handlers)
    // A component_processor event may have fired before this target mounted
    // (e.g. ScratchTicket's onReveal targeting a Timer lower in mount order).
    // Now that a new instance has registered, retry any deferred dispatches.
    flushPendingComponentDispatches()
  }

  const unregisterComponentProcessors = (instanceKey: string) => {
    componentProcessors.delete(instanceKey)
  }

  const getComponentProcessor = (
    instanceKey: string,
    processor: string,
  ): ComponentProcessorHandler | undefined => componentProcessors.get(instanceKey)?.[processor]

  // --- Deferred component-processor dispatch ---
  // A `component_processor` event can fire before its target component has
  // mounted and registered its handlers (page-mount ordering races, e.g.
  // ScratchTicket auto-reveal → Timer.start). Rather than dropping the call,
  // queue it and flush the instant a matching target registers. A TTL backstop
  // drops stale entries (target never mounts / page navigated away) and logs
  // the original "target not found" so nothing is ever *silently* lost.
  const PENDING_DISPATCH_TTL_MS = 4000
  interface PendingComponentDispatch {
    candidateKeys: string[]
    processor: string
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    args: any[]
    config: Record<string, unknown>
    targetPath: string[]
    sourceInstanceKey: string
    timeoutId: ReturnType<typeof setTimeout>
  }
  let pendingComponentDispatches: PendingComponentDispatch[] = []

  // Longest-prefix-first ordering is encoded by the caller; first hit wins.
  const resolveComponentProcessor = (
    candidateKeys: string[],
    processor: string,
  ): ComponentProcessorHandler | undefined => {
    for (const key of candidateKeys) {
      const handler = componentProcessors.get(key)?.[processor]
      if (handler) return handler
    }
    return undefined
  }

  const dispatchComponentProcessor = async (dispatch: {
    candidateKeys: string[]
    processor: string
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    args: any[]
    config: Record<string, unknown>
    targetPath: string[]
    sourceInstanceKey: string
  }): Promise<void> => {
    const handler = resolveComponentProcessor(dispatch.candidateKeys, dispatch.processor)
    if (handler) {
      await handler({ args: dispatch.args, config: dispatch.config, pipe: pipeInterface })
      return
    }
    // Target not registered yet — defer; flushed on the next matching register.
    const timeoutId = setTimeout(() => {
      pendingComponentDispatches = pendingComponentDispatches.filter(
        (d) => d.timeoutId !== timeoutId,
      )
      console.warn('[Pipe] component_processor: target not found', {
        processor: dispatch.processor,
        targetPath: dispatch.targetPath,
        sourceInstanceKey: dispatch.sourceInstanceKey,
      })
    }, PENDING_DISPATCH_TTL_MS)
    pendingComponentDispatches.push({ ...dispatch, timeoutId })
  }

  function flushPendingComponentDispatches() {
    if (pendingComponentDispatches.length === 0) return
    const stillPending: PendingComponentDispatch[] = []
    for (const d of pendingComponentDispatches) {
      const handler = resolveComponentProcessor(d.candidateKeys, d.processor)
      if (!handler) {
        stillPending.push(d)
        continue
      }
      clearTimeout(d.timeoutId)
      // Fire-and-forget — the originating event chain already moved on. Isolate
      // a throwing target so it can't escape as an unhandled rejection.
      void Promise.resolve(handler({ args: d.args, config: d.config, pipe: pipeInterface })).catch(
        (error) =>
          trackError({
            error,
            context: ERROR_CONTEXTS.eventProcessor,
            metadata: { processorType: 'component_processor', processor: d.processor },
          }),
      )
    }
    pendingComponentDispatches = stillPending
  }

  const clearPendingComponentDispatches = () => {
    for (const d of pendingComponentDispatches) clearTimeout(d.timeoutId)
    pendingComponentDispatches = []
  }
  // Drop deferred dispatches when leaving a page — their targets unmounted, so
  // they'd only ever expire via TTL with a misleading warning. The TTL remains
  // the backstop for same-page targets that never mount.
  subscribe('navigation:leave', () => clearPendingComponentDispatches())

  // The soft-keyboard dismissal lives on the `navigation:leave` event in
  // usePipe so it fires for every transition (incl. phone back/forward),
  // not just this app-initiated navigate.
  subscribe('navigation:leave', () => {
    if (typeof navigator === 'undefined' || typeof document === 'undefined') return
    setTimeout(() => {
      const active = document.activeElement as HTMLElement | null
      // Don't fight useAutoFocus: the new page's autofocus runs on navigation:show
      // (RAF, ~16ms) and lands before this +50ms keyboard-dismiss. If the active
      // element is a deliberate autofocus target, the new page focused it on
      // purpose — leave it. Keyed on the attribute (not mount lifecycle) so it also
      // covers persistent autofocus inputs in global shared nodes.
      if (active?.getAttribute('data-autofocus') === 'true') return
      active?.blur()
    }, 50)
  })

  // Editor-only commands — populated on mount by `useEditorCommands`. Keyed
  // by raw nodeId since the field editor targets concrete nodes (authoring
  // contexts don't address specific SharedNodeRef instances). Separate from
  // componentProcessors so these stay out of the user's event-wiring UI.
  const editorCommands = new Map<string, Record<string, EditorCommandHandler>>()

  // Merged rather than replaced: a node's own `useEditorCommands()` call and
  // RenderComponents' generic `previewAnimation` registration (useNodeAnimation.ts)
  // both target the same nodeId, and registering second must not clobber the first.
  const registerEditorCommands = (
    nodeId: string,
    handlers: Record<string, EditorCommandHandler>,
  ) => {
    const existing = editorCommands.get(nodeId)
    editorCommands.set(nodeId, existing ? { ...existing, ...handlers } : { ...handlers })
  }

  // Without `handlers`, drops the whole entry (back-compat). With it, only
  // removes the caller's own keys — so two registrants unmounting independently
  // don't wipe each other's still-live commands.
  const unregisterEditorCommands = (
    nodeId: string,
    handlers?: Record<string, EditorCommandHandler>,
  ) => {
    if (!handlers) {
      editorCommands.delete(nodeId)
      return
    }
    const existing = editorCommands.get(nodeId)
    if (!existing) return
    const next = { ...existing }
    for (const key of Object.keys(handlers)) delete next[key]
    if (Object.keys(next).length === 0) editorCommands.delete(nodeId)
    else editorCommands.set(nodeId, next)
  }

  const getEditorCommand = (nodeId: string, command: string): EditorCommandHandler | undefined =>
    editorCommands.get(nodeId)?.[command]

  // --- Form-group registry ---
  // Plain Map (not reactive) — the consumer is the imperative submit path,
  // not a template. Reactivity would only add overhead.
  interface FormGroupRecord {
    inputs: Map<string, FormInputEntry>
    // Multiple submit targets are allowed at runtime (edit-time validator
    // flags >=2 as an error, but if the author ignored it we must not
    // silently pick one — see `getFormSubmitTarget` below).
    submitTargets: Map<string, FormSubmitTarget>
  }
  const formGroups = new Map<string, FormGroupRecord>()
  const formGroupBySource = new Map<string, string>()

  const ensureFormGroup = (groupKey: string): FormGroupRecord => {
    let g = formGroups.get(groupKey)
    if (!g) {
      g = { inputs: new Map(), submitTargets: new Map() }
      formGroups.set(groupKey, g)
    }
    return g
  }

  const maybePruneFormGroup = (groupKey: string): void => {
    const g = formGroups.get(groupKey)
    if (!g) return
    if (g.inputs.size === 0 && g.submitTargets.size === 0) formGroups.delete(groupKey)
  }

  const registerFormInput = (groupKey: string, entry: FormInputEntry) => {
    ensureFormGroup(groupKey).inputs.set(entry.inputKey, entry)
  }

  const unregisterFormInput = (groupKey: string, inputKey: string) => {
    const g = formGroups.get(groupKey)
    if (!g) return
    g.inputs.delete(inputKey)
    maybePruneFormGroup(groupKey)
  }

  const registerFormSubmitTarget = (groupKey: string, target: FormSubmitTarget) => {
    const g = ensureFormGroup(groupKey)
    g.submitTargets.set(target.sourceKey, target)
    formGroupBySource.set(target.sourceKey, groupKey)
    if (g.submitTargets.size > 1) {
      console.warn(
        `[Pipe] Form group "${groupKey}" now has ${g.submitTargets.size} submit targets. ` +
          `Soft-keyboard Enter will no-op (validation only) until the ambiguity is resolved — ` +
          `edit-time validator flags this; mark all but one CTA with "Don't submit form on click".`,
      )
    }
  }

  const unregisterFormSubmitTarget = (groupKey: string, sourceKey: string) => {
    const g = formGroups.get(groupKey)
    if (!g) return
    g.submitTargets.delete(sourceKey)
    formGroupBySource.delete(sourceKey)
    maybePruneFormGroup(groupKey)
  }

  // Returns the sole submit target only when exactly one is registered.
  // With 0 → no submit component wired (legacy onSubmit fallback path).
  // With ≥2 → ambiguous; the caller (`useFormInput.requestSubmit`) falls back
  // to validation-only so we never invoke an arbitrary CTA the author may not
  // have intended as the keyboard-Enter target.
  const getFormSubmitTarget = (groupKey: string): FormSubmitTarget | null => {
    const g = formGroups.get(groupKey)
    if (!g || g.submitTargets.size !== 1) return null
    return g.submitTargets.values().next().value ?? null
  }

  const getFormGroupForSource = (sourceInstanceKey: string): string | undefined =>
    formGroupBySource.get(sourceInstanceKey)

  const submitFormGroup = async (groupKey: string): Promise<boolean> => {
    // Authoring preview never persists — leave transient edits uncommitted.
    if (currentMode.value === PipeDisplayMode.EDITOR) return true
    const group = formGroups.get(groupKey)
    // No registered inputs in this group → nothing of ours to validate or
    // commit. Crucially, do NOT flush all pending keys here — a transient edit
    // left on another step must not be persisted by this unrelated submit.
    if (!group || group.inputs.size === 0) return true
    // Force-show errors on every input regardless of realtime gating
    for (const e of group.inputs.values()) e.markTouched()
    // Run all validators concurrently
    const results = await Promise.all(
      [...group.inputs.values()].map((e) => Promise.resolve(e.validate())),
    )
    const ok = results.every(Boolean)
    // Promote ONLY this group's own transient edits, and only on a valid
    // submit, so invalid/incomplete data — and unrelated edits on other steps —
    // never reach disk.
    if (ok) {
      const keys = [...group.inputs.values()]
        .flatMap((e) => e.persistKeys?.() ?? [])
        .filter((k): k is string => !!k)
      commitVariables(keys)
    }
    return ok
  }

  // --- Exit-intent suppression registry ---
  // Plain Set (not reactive): read synchronously by the exit-intent detector's
  // trigger path, never rendered. Token-based so concurrent checkouts each hold
  // their own block; EI fires only when the set is empty.
  const exitIntentSuppressors = new Set<string>()
  const suppressExitIntent = (token: string) => {
    exitIntentSuppressors.add(token)
  }
  const releaseExitIntent = (token: string) => {
    exitIntentSuppressors.delete(token)
  }
  const isExitIntentSuppressed = () => exitIntentSuppressors.size > 0

  const getIntegrationConfig = <T = Record<string, unknown>>(type: string): T | undefined => {
    for (const ext of activeExtensions.value.values()) {
      if (ext.type === type) return (ext.config ?? undefined) as T | undefined
    }
    return undefined
  }

  const pipeInterface: PipeInterface = {
    initialize,
    registerExtensions,
    variables,
    data,
    translations,
    constants,
    isInitialized,
    activeExtensions,
    disableAllVisibilityRules,
    visibilityOverrides,
    editorAnimations,
    currentMode,
    setMode,
    activeNodeId,
    getPageById,
    getSharedNodeById,
    getData,
    getTranslation,
    setData,
    setVariableSilent,
    setTranslations,
    patchTranslation,
    setTranslation,
    setConstants,
    setSharedNodes,
    navigateToPageId,
    armNavigationRetry,
    getPageBySlug,

    markPageVisited,

    // Events
    emit,
    subscribe,
    events,
    eventHistory,
    trackError,
    // Event processors
    getAvailableProcessors,
    getEventProcessor,
    // Component-provided processor registry
    registerComponentProcessors,
    unregisterComponentProcessors,
    getComponentProcessor,
    dispatchComponentProcessor,
    // Editor-only command registry
    registerEditorCommands,
    unregisterEditorCommands,
    getEditorCommand,
    // Form-group registry
    registerFormInput,
    unregisterFormInput,
    registerFormSubmitTarget,
    unregisterFormSubmitTarget,
    getFormSubmitTarget,
    submitFormGroup,
    getFormGroupForSource,
    // Exit-intent suppression registry
    suppressExitIntent,
    releaseExitIntent,
    isExitIntentSuppressed,
    getIntegrationConfig,
    // Persistence
    savePersistedData,
    clearPersistedData,
    commitVariables,
    whenReady,
  }

  // 5. Bind to Window & Provide
  if (typeof window !== 'undefined') {
    // This makes window.pipe.setData() work immediately
    window.pipe = pipeInterface
  }

  // Apply the initial mode so window.pipeMode and the body `mode-*` class are in sync
  // with the `currentMode` ref from first render.
  setMode(currentMode.value)

  provide(PipeSymbol, pipeInterface)

  return pipeInterface
}

// --- Consumer Composable ---
export function usePipe(): PipeInterface {
  const pipe = inject(PipeSymbol)
  if (!pipe) {
    throw new Error('usePipe() must be called after setupPipeState()')
  }
  return pipe
}

export function isInPipeContext(): boolean {
  if (!hasInjectionContext()) return false
  const pipe = inject(PipeSymbol, null)
  return pipe !== null
}

/**
 * Returns the pipe interface if called from inside a pipe context,
 * otherwise null. Use this instead of `usePipe()` in components that
 * may also render outside a `setupPipeState()` tree (e.g. field editors).
 */
export function usePipeIfAvailable(): PipeInterface | null {
  if (!hasInjectionContext()) return null
  return inject(PipeSymbol, null)
}
